import React from "react";
import { ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/2b9rpxdv_couple%202.png";

function HeroVisual() {
  return (
    <div className="iw-scan-sweep relative overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-primary)]">
      <img
        src={HERO_IMG}
        alt="Two members with full-body scan and biometric data — every system measured, mapped, and personalized"
        className="block aspect-[4/5] w-full object-cover"
        loading="lazy"
        decoding="async"
      />
      {/* Subtle navy footer gradient so the bottom caption is legible */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
        style={{ background: "linear-gradient(to top, rgba(17,41,107,0.65) 0%, rgba(17,41,107,0.0) 100%)" }}
        aria-hidden
      />

      {/* Corner brackets */}
      <span className="pointer-events-none absolute left-4 top-4 h-4 w-4 border-l-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute right-4 top-4 h-4 w-4 border-r-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute left-4 bottom-4 h-4 w-4 border-l-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute right-4 bottom-4 h-4 w-4 border-r-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />

      <div className="absolute left-4 top-4 inline-flex items-center gap-2 border border-white/40 bg-[color:var(--iw-primary)]/80 px-3 py-1.5 font-mono text-[12px] uppercase tracking-[0.22em] text-white backdrop-blur">
        <span className="iw-pulse-dot block h-1.5 w-1.5 rounded-full bg-[color:var(--iw-accent-red)]" />
        Live Scan · 2 of 2
      </div>

      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
        <div className="border-l-2 border-[color:var(--iw-accent-red)] pl-4">
          <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-blue-bright)]">
            Every system · One plan
          </div>
          <div className="mt-1 font-display text-xl font-semibold leading-tight tracking-tight text-white sm:text-2xl">
            Mapped. Measured. <span className="text-[color:var(--iw-accent-red)]">Personalized.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OptimizeSystemsSection() {
  return (
    <section
      id="optimize-systems"
      className="relative overflow-hidden border-b border-[color:var(--iw-border)] bg-white py-20 sm:py-28"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-50" aria-hidden />
      <div
        className="pointer-events-none absolute -top-32 left-1/4 h-[500px] w-[500px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(30, 58, 138, 0.30), transparent 70%)" }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start lg:gap-14">
          {/* Image — 5 cols on desktop */}
          <div className="lg:col-span-5">
            <HeroVisual />
          </div>

          {/* Copy + systems — 7 cols on desktop */}
          <div className="lg:col-span-7">
            <div className="iw-overline mb-3">§ The vision</div>
            <h2 className="font-display text-3xl font-semibold leading-[1.05] tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl xl:text-[3.2rem]">
              Optimize Every System{" "}
              <span className="text-[color:var(--iw-blue)]">That Drives You</span>.
            </h2>

            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[color:var(--iw-body)]">
              You're not a fitness goal. You're not a sleep score. You're a connected system — and every part feeds the next. Your sleep shapes your hormones. Your hormones shape your training. Your training shapes your recovery. Your recovery shapes your sleep.
            </p>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-[color:var(--iw-body)]">
              Most programs optimize one corner. We optimize the whole loop — eight systems, one plan, every signal pulling in the same direction.
            </p>

            <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-5">
              <a
                href="#tracks"
                className="iw-btn-red inline-flex items-center gap-2 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em]"
              >
                See the Programs <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
